import {
  Box,
  Card,
  CollapsableCard,
  Icon,
  Tag,
  Text,
  Touchable,
} from '@factorialco/deprecated-design-system'
import {
  BudgetStatusCardFragment,
  FinanceValueObjectsBudgetStatusEnum,
} from 'generated/resources/graphql'
import { useToggle } from 'usehooks-ts'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { budgetStatusTag } from 'modules/expenses/lib/budgets/alerts'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

interface Props {
  budget: BudgetStatusCardFragment
}

const BudgetStatusCard = ({ budget }: Props) => {
  const {
    access: { locale },
  } = useCurrentSession()

  const overbudget = budget.currentStatus === 'over_budget'
  const [showContent, toggleContent] = useToggle(overbudget)

  const budgetDescription: Record<FinanceValueObjectsBudgetStatusEnum, string> = {
    within_budget: i18n.t('budgets.tooltips.expense_within_budget', {
      budget_name: budget.name,
    }),
    budget_risk: i18n.t('budgets.tooltips.expense_at_risk', { budget_name: budget.name }),
    over_budget: i18n.t('budgets.tooltips.expense_overbudget', {
      budget_name: budget.name,
    }),
  }
  const statusTag = budgetStatusTag(budget.currentStatus)

  return (
    <CollapsableCard
      open={showContent}
      contentBackground="grey000White"
      contentPadding="s8"
      Card={
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
          <Card.Title size="small">
            {i18n.t('budgets.forms.external_resource.budget_field.label')}
          </Card.Title>
          <Box gap="s16" flexDirection="row" alignItems="center">
            <Tag type={statusTag.color} inline>
              {statusTag.text}
            </Tag>
            <Touchable onClick={toggleContent}>
              <Icon.Small icon={showContent ? 'chevron-up' : 'chevron-down'} color="grey800" />
            </Touchable>
          </Box>
        </Box>
      }
      Content={
        <Box gap="s8" paddingX="s8">
          <Text.Normal size="200">{budgetDescription[budget.currentStatus]}</Text.Normal>

          <Box flexDirection="row" justifyContent="spaceBetween">
            <Box flexDirection="row" gap="s6" width="full">
              <Text size="200" weight="medium">
                {i18n.t('budgets.forms.external_resource.budget_field.label')}:
              </Text>
              <Text size="200" weight="medium" color="grey1000">
                {amountWithCurrency(Number(budget.amountCents), budget.currency, locale)}
              </Text>
            </Box>

            <Box flexDirection="row" gap="s6" width="full">
              <Text size="200" weight="medium">
                {i18n.t('budgets.detail.period_status.amount.reported')}:
              </Text>
              <Text size="200" weight="medium" color="grey1000">
                {amountWithCurrency(Number(budget.spentAmountCents), budget.currency, locale)}
              </Text>
            </Box>
          </Box>

          <Box flexDirection="row" gap="s6">
            <Text size="200" weight="medium">
              {i18n.t('budgets.detail.period_status.amount.available')}:
            </Text>
            <Text size="200" weight="medium" color={overbudget ? 'radicalRed1200' : 'grey1000'}>
              {amountWithCurrency(Number(budget.availableAmountCents), budget.currency, locale)}
            </Text>
          </Box>
        </Box>
      }
    />
  )
}

export default BudgetStatusCard
